import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'About', path: '/about' },
    { name: 'Programs', path: '/programs' },
    { name: 'Accelerator', path: '/accelerator' },
    { name: 'Book Cameron', path: '/book' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  // Pages with dark hero backgrounds where nav should be transparent+white
  const darkHeroPages = ['/', '/programs', '/contact', '/accelerator'];
  const hasDarkHero = darkHeroPages.includes(location.pathname);
  const useTransparent = hasDarkHero && !scrolled;

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        useTransparent ? 'bg-transparent' : 'glass-header shadow-sm'
      }`}
      data-testid="main-navigation"
    >
      <div className="container-premium">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <Link to="/" className="flex flex-col" data-testid="nav-logo">
            <span className={`font-display text-xl lg:text-2xl font-bold tracking-tight transition-colors duration-300 ${
              useTransparent ? 'text-white' : 'text-[#0B1F3A]'
            }`}>
              Legacy Leadership Group
            </span>
            <span className={`text-[10px] lg:text-xs font-body tracking-[0.15em] uppercase mt-0.5 font-semibold transition-colors duration-300 ${
              useTransparent ? 'text-[#6FAE3A]' : 'text-[#4F8A2F]'
            }`}>
              Building Leaders. Creating Legacy.
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-body text-sm font-medium transition-colors duration-200 ${
                  isActive(link.path) 
                    ? useTransparent ? 'text-[#6FAE3A]' : 'text-[#4F8A2F]'
                    : useTransparent ? 'text-white hover:text-[#6FAE3A]' : 'text-slate-600 hover:text-[#4F8A2F]'
                }`}
                data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </Link>
            ))}
            
            <Link to="/contact" data-testid="nav-cta">
              <Button className="bg-[#4F8A2F] hover:bg-[#6FAE3A] text-white rounded-full px-6 py-2.5 font-body text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg">
                Partner With Us
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2"
            data-testid="mobile-menu-button"
          >
            {isOpen ? (
              <X className={`w-6 h-6 ${useTransparent ? 'text-white' : 'text-[#0B1F3A]'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${useTransparent ? 'text-white' : 'text-[#0B1F3A]'}`} />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-6 border-t border-slate-100 bg-white" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`font-body text-base py-2 ${
                    isActive(link.path) ? 'text-[#4F8A2F] font-semibold' : 'text-slate-600'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              <Link to="/contact" onClick={() => setIsOpen(false)} className="mt-4">
                <Button className="w-full bg-[#4F8A2F] hover:bg-[#6FAE3A] text-white rounded-full py-4 font-body">
                  Partner With Us
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
